import React, { useState, useCallback, useEffect } from 'react'
import Select from 'react-select'
import { Badge, Button } from '../../../components'
import { JWPLAYER_SITE_ID, JWPLAYER_DEFAULT_PLAYER_ID, JWPLAYER_PLAYERS } from '../../../constants/jwplayer'
import formStyles from '../../../styles/forms.module.scss'
import selectStyles from '../../../styles/react-select.module.scss'
import styles from './VideoEmbedEditor.module.scss'

const PLAYER_OPTIONS = JWPLAYER_PLAYERS.map(p => ({ value: p.id, label: p.name }))

export interface VideoEmbedMetadata {
  video_id?: string
  player_id?: string
  title?: string
  description?: string
  duration?: number | null
}

interface VideoEmbedEditorProps {
  metadata: VideoEmbedMetadata
  onChange: (metadata: VideoEmbedMetadata) => void
}

/** Format seconds into MM:SS or HH:MM:SS */
const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

const VideoEmbedEditor: React.FC<VideoEmbedEditorProps> = ({ metadata, onChange }) => {
  const [fetching, setFetching] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  const update = useCallback(
    (patch: Partial<VideoEmbedMetadata>) => onChange({ ...metadata, ...patch }),
    [metadata, onChange],
  )

  // Auto-select the default player when none is set
  useEffect(() => {
    if (!metadata.player_id) update({ player_id: JWPLAYER_DEFAULT_PLAYER_ID })
  }, [metadata.player_id, update])

  const fetchFromJWPlayer = async () => {
    const videoId = metadata.video_id?.trim()
    if (!videoId) { setFetchError('Enter a Video ID first'); return }
    setFetching(true)
    setFetchError(null)
    try {
      const res = await fetch(
        `https://cdn.jwplayer.com/v2/media/${videoId}?site_id=${JWPLAYER_SITE_ID}`,
      )
      if (!res.ok) throw new Error(res.status === 404 ? 'Video not found' : `JWPlayer API error (${res.status})`)
      const data = await res.json()
      const item = data.playlist?.[0]
      if (!item) throw new Error('No playlist data returned')
      const patch: Partial<VideoEmbedMetadata> = {}
      if (item.duration) patch.duration = Math.round(item.duration)
      if (item.title && !metadata.title) patch.title = item.title
      if (item.description && !metadata.description) patch.description = item.description
      onChange({ ...metadata, ...patch })
    } catch (err) {
      setFetchError(err instanceof Error ? err.message : 'Failed to fetch video info')
    } finally {
      setFetching(false)
    }
  }

  return (
    <>
      <div className={formStyles.fieldGroup}>
        <label>Video ID <Badge variant="neutral" size="small">Internal</Badge></label>
        <div className={styles.videoIdRow}>
          <input
            type="text"
            value={metadata.video_id || ''}
            onChange={(e) => update({ video_id: e.target.value })}
            placeholder="e.g., abc12345"
          />
          <Button
            size="small"
            variant="secondary"
            onClick={fetchFromJWPlayer}
            disabled={fetching || !metadata.video_id?.trim()}
          >
            {fetching ? 'Fetching...' : 'Fetch Info'}
          </Button>
        </div>
        {fetchError && <p className={styles.fetchError}>{fetchError}</p>}
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Player <Badge variant="neutral" size="small">Internal</Badge></label>
        <Select
          value={PLAYER_OPTIONS.find(o => o.value === metadata.player_id) || null}
          onChange={(opt) => opt && update({ player_id: opt.value })}
          options={PLAYER_OPTIONS}
          className={selectStyles.reactSelect}
          classNamePrefix="react-select"
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Title <Badge variant="secondary" size="small">Public</Badge></label>
        <input
          type="text"
          value={metadata.title || ''}
          onChange={(e) => update({ title: e.target.value })}
          placeholder="Video title"
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>Description <Badge variant="secondary" size="small">Public</Badge></label>
        <textarea
          value={metadata.description || ''}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="Video description"
          rows={3}
        />
      </div>

      <div className={formStyles.fieldGroup}>
        <label>
          Duration
          {metadata.duration != null && (
            <span className={styles.durationDisplay}>{formatDuration(metadata.duration)}</span>
          )}
        </label>
        <input
          type="number"
          value={metadata.duration ?? ''}
          onChange={(e) => update({ duration: e.target.value ? parseInt(e.target.value, 10) : null })}
          placeholder="Seconds (use Fetch Info to auto-fill)"
          min={0}
        />
      </div>

      {metadata.video_id && (
        <div className={formStyles.fieldGroup}>
          <label>Thumbnail Preview</label>
          <div className={styles.preview}>
            <img
              src={`https://cdn.jwplayer.com/v2/media/${metadata.video_id}/poster.jpg?width=640`}
              alt={metadata.title || metadata.video_id}
              className={styles.previewImage}
            />
          </div>
        </div>
      )}
    </>
  )
}

export default VideoEmbedEditor
